import { isUfeEnvProduction } from '@ufe/shared/constants/env';
import localeUtils from 'utils/LanguageLocale';
import anaUtils from 'analytics/utils';
import basketUtils from 'utils/Basket';
import skuUtils from 'utils/Sku';
import orderUtils from 'utils/Order';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import Location from 'utils/Location';

/** Confirmation commerce actions that must not double-fire for the same orderId in a session. */
const ROKT_SESSION_DEDUPE_ACTIONS = new Set(['CheckoutOption', 'Purchase']);
const pendingRoktCommerceEvents = new Set();
const ROKT_LOYALTY_TIERS = Object.freeze({
    BI: 'Insider',
    VIB: 'VIB',
    ROUGE: 'Rouge'
});

const getRoktCommerceEventKey = (actionName, orderId) => `${actionName}:${orderId}`;

const getPersistedRoktCommerceEvents = () => {
    try {
        return Storage.session.getItem(LOCAL_STORAGE.ROKT_COMMERCE_EVENTS_FIRED) || {};
    } catch {
        return {};
    }
};

const claimRoktOrderCommerceEvent = (actionName, orderId) => {
    const key = getRoktCommerceEventKey(actionName, orderId);

    if (pendingRoktCommerceEvents.has(key) || getPersistedRoktCommerceEvents()[key]) {
        return false;
    }

    pendingRoktCommerceEvents.add(key);

    return true;
};

const releaseRoktOrderCommerceEvent = (actionName, orderId) => {
    pendingRoktCommerceEvents.delete(getRoktCommerceEventKey(actionName, orderId));
};

const persistRoktOrderCommerceEvent = (actionName, orderId) => {
    const key = getRoktCommerceEventKey(actionName, orderId);
    const fired = getPersistedRoktCommerceEvents();
    fired[key] = true;

    try {
        Storage.session.setItem(LOCAL_STORAGE.ROKT_COMMERCE_EVENTS_FIRED, fired);
    } catch {
        // Best-effort session persistence only.
    }
};

/** Clears in-memory + session Rokt commerce dedupe state (for unit tests). */
export const resetRoktCommerceEventDedupe = () => {
    pendingRoktCommerceEvents.clear();
    roktIdentityReady = Promise.resolve();

    try {
        Storage.session.removeItem(LOCAL_STORAGE.ROKT_COMMERCE_EVENTS_FIRED);
    } catch {
        // ignore
    }
};

// Tracks the URL of the last page that fired a Rokt page view so SPA navigations
// produce correct referringpage values instead of the stale document.referrer.
// null signals "not yet initialized" — seeded from document.referrer on first call.
let previousPageUrl = null;

// Shared Promise that resolves when the current identity call (identify/login) completes.
// fireRoktPageView awaits this so page view events are always serialized after identity.
let roktIdentityReady = Promise.resolve();

/** Returns the environment-specific Rokt placement identifier for the given placement name. */
export const getRoktIdentifier = name => {
    return {
        confirmation: isUfeEnvProduction ? 'prod.rokt.confirmation' : 'stg.rokt.confirmation',
        payments: isUfeEnvProduction ? 'prod.rokt.payments' : 'stg.rokt.payments'
    }[name];
};

/** Returns true when Rokt SDK+ is enabled and the user is in the US. */
export const isSdkPlusEnabled = () => !!(Sephora.configurationSettings?.smn?.roktSDKPlus?.isEnabled && localeUtils.isUS());

/**
 * Maps UFE's BI/VIB/ROUGE segments to Rokt's Insider/VIB/Rouge values.
 * @param {string} vibSegment - UFE Beauty Insider segment
 * @returns {string|null} Rokt loyalty tier, or null for guests/non-members
 */
export const getRoktLoyaltyTier = vibSegment => {
    const normalizedTier = String(vibSegment || '')
        .trim()
        .toUpperCase();

    return ROKT_LOYALTY_TIERS[normalizedTier] || null;
};

/**
 * Returns true when the current page is in scope for Rokt wishlist commerce events.
 * Ticket scope: Home, PDP (incl. Related Items), Category, Search, Brand, Beauty Preferences.
 * MyListsModal is global — callers must gate with this before firing wishlist events.
 */
export const isRoktWishlistEligiblePage = () =>
    Location.isHomepage() ||
    Location.isProductPage() ||
    Location.isRootCategoryPage() ||
    Location.isNthCategoryPage() ||
    Location.isSearchPage() ||
    Location.isBrandNthCategoryPage() ||
    Location.isBeautyPreferencesPage() ||
    Location.isBeautyPreferencesWorldPage();

const parseRoktMoney = value => {
    if (value == null || value === '') {
        return 0;
    }

    // Numeric amounts (e.g. orderTotal: 55).
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    if (String(value).toUpperCase() === 'FREE') {
        return 0;
    }

    // Use Basket.removeCurrency so "$1,234.56" / "$49" parse correctly (parsePrice does not).
    const parsed = Number(basketUtils.removeCurrency(String(value)));

    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Parses a money string for Rokt product unit price.
 * Returns 0 only for explicitly free values ("FREE"); missing/invalid values return null
 * so callers can omit the product instead of zero-padding.
 */
const parseRoktProductMoney = value => {
    if (value == null || value === '') {
        return null;
    }

    // Numeric amounts (including 0).
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    if (String(value).toUpperCase() === 'FREE') {
        return 0;
    }

    let amount = String(value);

    // If there are no digits, Basket.removeCurrency will coerce the value to 0;
    // treat as invalid so we don't emit $0 prices.
    if (!/\d/.test(amount)) {
        return null;
    }

    // Catalog price ranges (e.g. "$28.00 - $48.00"): removeCurrency collapses "28.00-48.00" to 0.
    // Rokt expects the default/min SKU price — use the left-hand amount from the range.
    if (/\d(?:[.,]\d+)?\s*-\s*\$?\s*\d/.test(amount)) {
        amount = amount.split(/\s*-\s*/)[0];
    }

    const parsed = Number(basketUtils.removeCurrency(amount));

    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Resolves Rokt per-unit price from paid line totals when available (matches analytics
 * orderBindingUtils), falling back to catalog sku price. Samples/BI rewards are 0.
 * Uses ?? so a paid line total of 0 is preserved (not treated as missing).
 */
const getRoktUnitPrice = (productOrSku, sku, quantity) => {
    if (skuUtils.isSample(sku) || skuUtils.isBiReward(sku)) {
        return 0;
    }

    const lineTotal = productOrSku.salePriceSubtotal ?? productOrSku.itemTotal ?? productOrSku.listPriceSubtotal;

    if (lineTotal != null && lineTotal !== '') {
        const parsedLineTotal = parseRoktProductMoney(lineTotal);

        if (parsedLineTotal != null) {
            return parsedLineTotal / quantity;
        }
    }

    return parseRoktProductMoney(sku.salePrice ?? sku.listPrice);
};

/**
 * Normalizes a Sephora sku/product/order-item shape into fields needed for a Rokt Product object.
 * Accepts a flat sku, a product with `currentSku`, or an order/basket item with nested `sku` + `qty`.
 * Price is per-unit paid amount (line total / qty) when line totals exist.
 */
const normalizeRoktProductData = productOrSku => {
    if (!productOrSku || typeof productOrSku !== 'object') {
        return null;
    }

    const sku = productOrSku.currentSku || productOrSku.sku || productOrSku;
    // Catalog payloads (e.g. ProductTile) keep the name/brand on the product, not on currentSku
    const productName = sku.productName || productOrSku.productName || productOrSku.displayName || productOrSku.productDetails?.displayName;
    const skuId = sku.skuId;

    if (!productName || !skuId) {
        return null;
    }

    const quantity = productOrSku.qty || productOrSku.quantity || 1;
    const price = getRoktUnitPrice(productOrSku, sku, quantity);

    // Rokt requires a valid itemprice; skip items we cannot price (except explicit free = 0).
    if (price == null) {
        return null;
    }

    return {
        name: productName,
        sku: skuId,
        price,
        quantity,
        variant: sku.variationValue || undefined,
        category: sku.categoryName || productOrSku.parentCategory?.displayName || undefined,
        brand: sku.brandName || productOrSku.brandName || productOrSku.productDetails?.brand?.displayName || undefined
    };
};

const buildRoktProducts = (eCommerce, productsOrSku) => {
    const productList = Array.isArray(productsOrSku) ? productsOrSku : [productsOrSku];

    return productList
        .map(normalizeRoktProductData)
        .filter(Boolean)
        .map(productData =>
            eCommerce.createProduct(
                productData.name,
                productData.sku,
                productData.price,
                productData.quantity,
                productData.variant,
                productData.category,
                productData.brand
            )
        );
};

const getRoktCouponCode = orderDetails => {
    const appliedPromotions = orderDetails?.promotion?.appliedPromotions || [];
    const promoCodes = appliedPromotions.map(promo => promo.couponCode || promo.promotionId).filter(Boolean);

    return promoCodes.length ? promoCodes.join(',') : undefined;
};

/**
 * Maps order fulfillment to Rokt `shippingmethod` labels from the ticket:
 * BOPIS, SDD, Standard, Business, Expedited, Priority.
 * Handles production shippingMethodType values (e.g. "Express", "Express Shipping").
 */
export const getRoktShippingMethod = orderDetails => {
    const header = orderDetails?.header || {};

    if (header.isBopisOrder || header.isRopisOrder) {
        return 'BOPIS';
    }

    if (header.isSameDayOrder || orderUtils.getSameDayShippingGroup(orderDetails)) {
        return 'SDD';
    }

    const hardGoodGroup = orderUtils.getHardGoodShippingGroup(orderDetails);
    const methodType = (hardGoodGroup?.shippingMethod?.shippingMethodType || '').toLowerCase();

    // No hard-good method (e.g. electronic-only / incomplete payload) — skip CheckoutOption.
    if (!methodType) {
        return null;
    }

    if (methodType.includes('business')) {
        return 'Business';
    }

    if (methodType.includes('priority')) {
        return 'Priority';
    }

    // Production often uses "Express" / "Express Shipping"; ticket label is Expedited.
    if (methodType.includes('expedited') || methodType.includes('express') || methodType.includes('next day')) {
        return 'Expedited';
    }

    return 'Standard';
};

/** Shipping ZIP/postcode for Rokt CheckoutOption event attributes. */
export const getRoktShippingZip = orderDetails => {
    const header = orderDetails?.header || {};

    if (header.isBopisOrder || header.isRopisOrder) {
        const storeDetails = orderDetails?.pickup?.storeDetails;
        const storeZip = storeDetails?.address?.postalCode || storeDetails?.postalCode;

        if (storeZip) {
            return String(storeZip);
        }
    }

    const shipGroups = orderDetails?.shippingGroups?.shippingGroupsEntries || [];
    const zipFromGroups = orderUtils.getZipCode(shipGroups);

    if (zipFromGroups) {
        return String(zipFromGroups);
    }

    const sameDayGroup = orderUtils.getSameDayShippingGroup(orderDetails);
    const hardGoodGroup = orderUtils.getHardGoodShippingGroup(orderDetails);

    return sameDayGroup?.address?.postalCode || hardGoodGroup?.address?.postalCode || '';
};

/** Rokt CheckoutOption (shipping) event-level attributes per Web SDK+ contract. */
const buildRoktCheckoutOptionAttributes = orderDetails => ({
    currency: localeUtils.ISO_CURRENCY.US,
    option: 'shipping',
    shippingmethod: getRoktShippingMethod(orderDetails),
    zipcode: getRoktShippingZip(orderDetails),
    totalprice: parseRoktMoney(orderDetails?.priceInfo?.orderTotal),
    // Event-level country (separate from user attribute); ISO 3166-1 alpha-2.
    country: localeUtils.COUNTRIES.US
});

/** Rokt Checkout event-level attributes. totalprice is subtotal before tax/shipping. */
const buildRoktCheckoutAttributes = (orderDetails, items) => ({
    currency: localeUtils.ISO_CURRENCY.US,
    totalprice: parseRoktMoney(orderDetails?.priceInfo?.merchandiseSubtotal),
    cartitemcount: orderDetails?.items?.itemCount ?? items.length
});

const buildRoktTransactionAttributes = orderDetails => {
    const orderId = orderDetails?.header?.orderId;

    if (!orderId) {
        return null;
    }

    const priceInfo = orderDetails.priceInfo || {};

    return {
        orderId,
        revenue: parseRoktMoney(priceInfo.orderTotal),
        tax: parseRoktMoney(priceInfo.tax ?? priceInfo.stateTax),
        shipping: parseRoktMoney(priceInfo.totalShipping),
        couponCode: getRoktCouponCode(orderDetails)
    };
};

const createMParticleTransactionAttributes = (eCommerce, transactionAttributes) => {
    if (!transactionAttributes) {
        return null;
    }

    let txnAttrs;

    if (typeof eCommerce.createTransactionAttributes === 'function') {
        txnAttrs = eCommerce.createTransactionAttributes(
            transactionAttributes.orderId,
            transactionAttributes.affiliation,
            transactionAttributes.couponCode,
            transactionAttributes.revenue,
            transactionAttributes.shipping,
            transactionAttributes.tax
        );
    } else {
        txnAttrs = {
            Id: transactionAttributes.orderId,
            Affiliation: transactionAttributes.affiliation,
            CouponCode: transactionAttributes.couponCode,
            Revenue: transactionAttributes.revenue,
            Shipping: transactionAttributes.shipping,
            Tax: transactionAttributes.tax
        };
    }

    if (transactionAttributes.step != null) {
        txnAttrs.Step = transactionAttributes.step;
    }

    return txnAttrs;
};

/**
 * Fires a Rokt SDK+ commerce product action via mParticle.eCommerce.logProductAction.
 * Resolves ProductActionType inside mParticle.ready so events still queue while the SDK loads.
 * CheckoutOption/Purchase are session-deduped by orderId after a successful logProductAction.
 * No-ops when SDK+ is disabled, user is not in the US, mParticle is not ready,
 * product data is incomplete, or the requested ProductActionType is unavailable.
 *
 * @param {string} actionName - ProductActionType key (e.g. 'Checkout', 'Purchase')
 * @param {object|object[]} productsOrSku - Sephora sku, product, order item, or array of those
 * @param {object} [transactionAttributes] - TransactionAttributes for checkout/purchase events
 * @param {object} [customAttributes] - Optional custom attributes for the product action
 */
export const fireRoktCommerceEvent = (actionName, productsOrSku, transactionAttributes = null, customAttributes = null) => {
    if (!isSdkPlusEnabled() || !actionName || typeof window === 'undefined' || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    const orderId = transactionAttributes?.orderId;
    const shouldDedupe = ROKT_SESSION_DEDUPE_ACTIONS.has(actionName) && !!orderId;

    if (shouldDedupe && !claimRoktOrderCommerceEvent(actionName, orderId)) {
        return;
    }

    window.mParticle.ready(() => {
        const productActionType = window.mParticle.ProductActionType?.[actionName];

        if (productActionType == null) {
            if (shouldDedupe) {
                releaseRoktOrderCommerceEvent(actionName, orderId);
            }

            return;
        }

        const { eCommerce } = window.mParticle;

        if (typeof eCommerce?.createProduct !== 'function' || typeof eCommerce?.logProductAction !== 'function') {
            if (shouldDedupe) {
                releaseRoktOrderCommerceEvent(actionName, orderId);
            }

            return;
        }

        const products = buildRoktProducts(eCommerce, productsOrSku);

        if (!products.length) {
            if (shouldDedupe) {
                releaseRoktOrderCommerceEvent(actionName, orderId);
            }

            return;
        }

        const txnAttrs = createMParticleTransactionAttributes(eCommerce, transactionAttributes);

        eCommerce.logProductAction(productActionType, products, customAttributes || null, null, txnAttrs);

        if (shouldDedupe) {
            persistRoktOrderCommerceEvent(actionName, orderId);
        }
    });
};

const fireRoktOrderCommerceEvent = (actionName, orderDetails, { includeOption = false, includeCheckoutAttrs = false } = {}) => {
    const items = orderDetails?.items?.items;

    if (!items?.length) {
        return;
    }

    const transactionAttributes = buildRoktTransactionAttributes(orderDetails);

    if (!transactionAttributes) {
        return;
    }

    let customAttributes = null;

    if (includeOption) {
        // Rokt CheckoutOption: currency/option/shippingmethod/zipcode/totalprice are event-level attributes.
        customAttributes = buildRoktCheckoutOptionAttributes(orderDetails);
    } else if (includeCheckoutAttrs) {
        // Rokt Checkout: currency + totalprice (pre tax/shipping) + cartitemcount.
        customAttributes = buildRoktCheckoutAttributes(orderDetails, items);
    } else {
        // Purchase (and other order commerce events): currency is a required event-level attribute.
        customAttributes = { currency: localeUtils.ISO_CURRENCY.US };
    }

    fireRoktCommerceEvent(actionName, items, transactionAttributes, customAttributes);
};

/**
 * Fires Rokt SDK+ Checkout commerce event (US + kill-switch gated).
 * @param {object} orderDetails - Sephora order details
 */
export const fireRoktCheckout = orderDetails => {
    fireRoktOrderCommerceEvent('Checkout', orderDetails, { includeCheckoutAttrs: true });
};

/**
 * Fires Rokt SDK+ CheckoutOption commerce event (US + kill-switch gated).
 * Send only after purchase is made (Order Confirmation).
 * Skips when no shipping method can be resolved (e.g. electronic-only orders).
 * @param {object} orderDetails - Sephora order details
 */
export const fireRoktCheckoutOption = orderDetails => {
    if (!getRoktShippingMethod(orderDetails)) {
        return;
    }

    fireRoktOrderCommerceEvent('CheckoutOption', orderDetails, { includeOption: true });
};

/**
 * Fires Rokt SDK+ Purchase commerce event (US + kill-switch gated).
 * @param {object} orderDetails - Sephora order details
 */
export const fireRoktPurchase = orderDetails => {
    fireRoktOrderCommerceEvent('Purchase', orderDetails);
};

/**
 * Fires Rokt SDK+ AddToWishlist commerce event (US + kill-switch gated).
 * @param {object} productOrSku - Sephora sku or product object
 */
export const fireRoktAddToWishlist = productOrSku => {
    fireRoktCommerceEvent('AddToWishlist', productOrSku);
};

/**
 * Fires Rokt SDK+ RemoveFromWishlist commerce event (US + kill-switch gated).
 * @param {object} productOrSku - Sephora sku or product object
 */
export const fireRoktRemoveFromWishlist = productOrSku => {
    fireRoktCommerceEvent('RemoveFromWishlist', productOrSku);
};

/**
 * Fires Rokt SDK+ ViewDetail commerce event (US + kill-switch gated).
 * Call when a PDP loads (initial or SPA nav to a new product).
 * @param {object} productOrSku - Sephora product or sku object
 */
export const fireRoktViewDetail = productOrSku => {
    fireRoktCommerceEvent('ViewDetail', productOrSku, null, { currency: localeUtils.ISO_CURRENCY.US });
};

/**
 * Fires Rokt SDK+ Click commerce event (US + kill-switch gated).
 * Call when a user clicks a product to navigate to its PDP.
 * @param {object} productOrSku - Sephora product or sku object
 */
export const fireRoktClick = productOrSku => {
    fireRoktCommerceEvent('Click', productOrSku, null, { currency: localeUtils.ISO_CURRENCY.US });
};

/**
 * Fires Rokt SDK+ AddToCart commerce event (US + kill-switch gated).
 * Fires for all item types including samples and BI rewards.
 * @param {object} productOrSku - Sephora sku or product, optionally with qty
 */
export const fireRoktAddToCart = productOrSku => {
    fireRoktCommerceEvent('AddToCart', productOrSku, null, { currency: localeUtils.ISO_CURRENCY.US });
};

/**
 * Fires Rokt SDK+ RemoveFromCart commerce event (US + kill-switch gated).
 * Fires for all item types including samples and BI rewards.
 * @param {object} productOrSku - Sephora sku or product
 */
export const fireRoktRemoveFromCart = productOrSku => {
    fireRoktCommerceEvent('RemoveFromCart', productOrSku, null, { currency: localeUtils.ISO_CURRENCY.US });
};

const buildRoktIdentityRequest = async profile => {
    const emailSource = (profile?.login || profile?.email || '').toLowerCase().trim();
    const emailHash = emailSource ? await anaUtils.hashString(emailSource) : null;

    return {
        userIdentities: {
            // eslint-disable-next-line camelcase
            email_sha256: emailHash,
            customerid: profile?.profileId || null
        }
    };
};

const getRoktCustomerType = isAuthenticated => {
    return typeof isAuthenticated === 'boolean' ? (isAuthenticated ? 'logged_in' : 'guest') : null;
};

/**
 * Updates the current Rokt SDK+ user when UFE's authentication state becomes known.
 * @param {boolean} [isAuthenticated] - True for authenticated users, false for guests, or omitted when unavailable
 */
export const setRoktCustomerType = isAuthenticated => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    window.mParticle.ready(() => {
        const user = window.mParticle.Identity?.getCurrentUser?.();

        if (user) {
            user.setUserAttribute('customertype', getRoktCustomerType(isAuthenticated));
        }
    });
};

const setRoktUserAttributes = (result, profile, { isAuthenticated } = {}) => {
    const user = result.getUser();

    if (!user) {
        Sephora.logger.warn('[Rokt] mParticle identity failed, proceeding without user attributes');

        return;
    }

    const hasProfileData = !!(profile?.profileId || profile?.login || profile?.email);
    const biAccount = hasProfileData ? profile?.beautyInsiderAccount : null;

    user.setUserAttribute('customertype', getRoktCustomerType(isAuthenticated));
    user.setUserAttribute('loyaltytier', getRoktLoyaltyTier(biAccount?.vibSegment));

    // Non-PII ISO 3166-1 alpha-2 country; SDK+ is US-only (isSdkPlusEnabled).
    user.setUserAttribute('country', localeUtils.COUNTRIES.US);

    if (!hasProfileData) {
        return;
    }

    if (profile?.firstName) {
        user.setUserAttribute('firstname', profile.firstName);
    }

    if (profile?.lastName) {
        user.setUserAttribute('lastname', profile.lastName);
    }

    if (biAccount?.biAccountId) {
        user.setUserAttribute('loyaltyid', biAccount.biAccountId);
    }
};

/**
 * Fires a Rokt SDK+ identity login event (login or registration).
 * Use when a user explicitly authenticates.
 */
export const fireRoktLogin = profile => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    roktIdentityReady = new Promise(resolve => {
        window.mParticle.ready(async () => {
            if (!profile?.profileId && !profile?.login && !profile?.email) {
                resolve();

                return;
            }

            try {
                const identityRequest = await buildRoktIdentityRequest(profile);

                window.mParticle.Identity.login(identityRequest, result => {
                    setRoktUserAttributes(result, profile, { isAuthenticated: true });
                    resolve();
                });
            } catch (err) {
                Sephora.logger.error('[Rokt] login failed:', err);
                resolve();
            }
        });
    });
};

/**
 * Fires a Rokt SDK+ identity event for the current user.
 * Handles both logged-in users (profile with profileId/email) and guests.
 * Call from UserInfo.js to establish the current identity, or when a non-login identifier
 * such as a guest checkout email becomes available.
 *
 * @param {object|null} profile - Profile data used to build the Rokt identity request
 * @param {object} [options] - Identity context
 * @param {boolean} [options.isAuthenticated] - Whether UFE currently considers the user authenticated
 * @returns {Promise<void>} Resolves after the identity callback completes
 */
export const fireRoktIdentity = (profile, { isAuthenticated } = {}) => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return Promise.resolve();
    }

    roktIdentityReady = new Promise(resolve => {
        window.mParticle.ready(async () => {
            try {
                const identityRequest = await buildRoktIdentityRequest(profile);

                window.mParticle.Identity.identify(identityRequest, result => {
                    setRoktUserAttributes(result, profile, { isAuthenticated });
                    resolve();
                });
            } catch (err) {
                Sephora.logger.error('[Rokt] identity failed:', err);
                resolve();
            }
        });
    });

    return roktIdentityReady;
};

/**
 * Fires a Rokt SDK+ page view event via mParticle.logPageView.
 * No-ops when SDK+ is disabled, user is not in the US, or mParticle is not ready.
 * Safe to call from useEffect and componentDidMount.
 *
 * @param {object} options
 * @param {string} options.pagetype   - Rokt pagetype value (e.g. 'Auth', 'MyAccount')
 * @param {string} options.screenname - Rokt screenname value (e.g. 'Sign In')
 * @param {string} options.title      - Hardcoded page title per Rokt spec
 */
export const fireRoktPageView = ({ pagetype, screenname, title }) => {
    // Seed from document.referrer on the very first call (always runs browser-side from useEffect/componentDidMount).
    if (previousPageUrl === null) {
        previousPageUrl = document.referrer;
    }

    const currentUrl = window.location.toString();
    const referringpage = previousPageUrl;
    previousPageUrl = currentUrl;

    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    const attributes = {
        screenname,
        pagetype,
        title,
        url: currentUrl,
        referringpage
    };

    window.mParticle.ready(async () => {
        await roktIdentityReady;
        window.mParticle.logPageView('page view', attributes);
    });
};

/**
 * Calls mParticle.Rokt.selectPlacements inside a mParticle.ready callback.
 * Returns a Promise that resolves to the Rokt selection, or null when SDK+ is disabled.
 * The caller is responsible for _mounted checks around the awaited result.
 *
 * @param {object} options
 * @param {string} options.identifier - Rokt placement identifier
 * @param {object} options.attributes - Placement attributes
 * @returns {Promise<object|null>}
 */
export const fireRoktSelectPlacements = ({ identifier, attributes }) => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
        window.mParticle.ready(async () => {
            try {
                await roktIdentityReady;
                const selection = await window.mParticle.Rokt.selectPlacements({ identifier, attributes });
                resolve(selection);
            } catch (err) {
                reject(err);
            }
        });
    });
};

/**
 * Logs the current SDK+ user out and initializes the resulting anonymous user attributes.
 */
export const fireRoktLogout = () => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    window.mParticle.ready(() => {
        window.mParticle.Identity.logout({}, result => {
            setRoktUserAttributes(result, {}, { isAuthenticated: false });
        });
    });
};
